import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, get, post } from '@/lib/api/client';
import { downloadResponse } from '@/lib/download';
import type {
  FlagsInput,
  InventoryHistory,
  InventoryItem,
  InventorySearchResult,
  MovementInput,
} from './types';

/**
 * One store's stock.
 *
 * Everything here is keyed by `businessItemId` — the row joining an item to a
 * store — not by the catalogue item, so the same product in two stores is two
 * independent quantities.
 */

const KEY = ['inventory'] as const;

export const PAGE_SIZE = 25;

export interface InventoryQuery {
  businessId: string;
  query: string;
  divisions: string[];
  groups: string[];
  departmentId: string | null;
  categoryId: string | null;
  sorting: number;
  page: number;
}

export function useInventorySearch(params: InventoryQuery, enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'search', params],
    queryFn: () =>
      post<InventorySearchResult>('inventory/search', {
        BusinessId: params.businessId,
        Page: params.page,
        Size: PAGE_SIZE,
        Query: params.query.trim(),
        SelectedDepartmentId: params.departmentId,
        SelectedCategoryId: params.categoryId,
        Divisions: params.divisions,
        Groups: params.groups,
        SelectedCategoriesId: [],
        SelectedDepartmentsId: [],
        Sorting: params.sorting,
      }),
    enabled,
    staleTime: 30_000,
  });
}

/** One row, re-read after a write so the drawer shows what was stored. */
export function useInventoryItem(businessItemId: string | null) {
  return useQuery({
    queryKey: [...KEY, 'item', businessItemId],
    queryFn: () => get<InventoryItem>(`inventory/item/${businessItemId}`),
    enabled: businessItemId !== null,
  });
}

export function useInventoryHistory(
  businessItemId: string | null,
  page: number
) {
  return useQuery({
    queryKey: [...KEY, 'history', businessItemId, page],
    queryFn: () =>
      get<InventoryHistory>(
        `inventory/history/${businessItemId}/${page}/${PAGE_SIZE}`
      ),
    enabled: businessItemId !== null,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: KEY });
}

/**
 * Record a movement.
 *
 * `Sale` exists on the enum but is the till's to write; this page only ever
 * sends the three a person can perform by hand.
 */
export function useRegisterMovement() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (values: MovementInput) =>
      post<unknown>('inventory/register', {
        BusinessItemId: values.businessItemId,
        Amount: values.amount,
        Comments: values.comments,
        InventoryChangeDate: values.inventoryChangeDate,
        Type: values.type,
        Reason: values.reason,
      }),
    onSuccess: invalidate,
  });
}

/**
 * Undo one movement.
 *
 * The service subtracts the movement's amount from the quantity on hand, then
 * **deletes the movement row** — so the correction leaves no trace, and the
 * item's history afterwards no longer explains its own total. It also flips
 * `IsAvailable` to `QuantityOnHand > 0` as a side effect. Both are why the
 * confirmation spells out what happens.
 */
export function useRevertMovement() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (movementId: string) =>
      post<unknown>(`inventory/revert/${movementId}`, {}),
    onSuccess: invalidate,
  });
}

/**
 * Rebuild the count from the movement rows.
 *
 * `CalculateQuantityOnHandAsync` sums every `InventoryMovement.Amount` for the
 * item and overwrites `QuantityOnHand` with the total — the reconciliation tool
 * for when the running count and its own history have drifted apart.
 *
 * Two things it does quietly: it is a **no-op when `AffectsInventory` is off**
 * (and still reports success), and it derives from the movements that still
 * exist, so anything a revert deleted is gone from the arithmetic for good.
 *
 * The route is `item/qoh/{id}`. The original posted to `item/{id}`, which is
 * registered for GET only — so its "Calc QOH" button has been answering 405 and
 * then skipping the refresh that followed it.
 */
export function useRecalculateQoh() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (businessItemId: string) =>
      post<unknown>(`inventory/item/qoh/${businessItemId}`, {}),
    onSuccess: invalidate,
  });
}

/**
 * The items this one packs into, or is packed from.
 *
 * Answered as full inventory rows, so each carries its own `pack` — the number
 * of units of *this* item that one of those is worth.
 */
export function useRelatedItems(businessItemId: string | null) {
  return useQuery({
    queryKey: [...KEY, 'related', businessItemId],
    queryFn: () =>
      get<InventoryItem[]>(`inventory/related/${businessItemId}`),
    select: (data) => data ?? [],
    enabled: businessItemId !== null,
  });
}

/** One movement per related item, applied together. */
export interface BreakdownEntry {
  businessItemId: string;
  add: number;
  remove: number;
}

export function useRegisterBreakdown() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (entries: BreakdownEntry[]) =>
      post<unknown>(
        'inventory/breakdown',
        entries.map((entry) => ({
          BusinessItemId: entry.businessItemId,
          Add: entry.add,
          Remove: entry.remove,
        }))
      ),
    onSuccess: invalidate,
  });
}

/** Partial update — only the fields passed are written. */
export function useUpdateFlags() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (values: FlagsInput) =>
      post<unknown>('inventory/update/item', {
        BusinessItemId: values.businessItemId,
        AffectsInventory: values.affectsInventory,
        IsVisible: values.isVisible,
        IsAvailable: values.isAvailable,
        QuantityOnHand: values.quantityOnHand,
        Cost: values.cost,
        CostOfBox: values.costOfBox,
        Pack: values.pack,
        Price: values.price,
      }),
    onSuccess: invalidate,
  });
}

/**
 * The store's catalogue as a CSV.
 *
 * Filtered the same way the list is, with one exception worth stating: the
 * endpoint takes no free-text term, so the search box does **not** narrow the
 * export. Everything else on the filter bar does.
 *
 * Answers with a `FileResult` on success and a JSON envelope on failure, both
 * as HTTP 200 — `downloadResponse` is what tells them apart.
 */
export function useExportCatalog() {
  return useMutation({
    mutationFn: (params: {
      businessId: string;
      divisions: string[];
      departmentId: string | null;
      categoryId: string | null;
    }) => {
      const search = new URLSearchParams();
      if (params.departmentId) search.set('departmentId', params.departmentId);
      if (params.categoryId) search.set('categoryId', params.categoryId);
      // Repeated keys rather than a joined string: the action binds these
      // `[FromQuery] IEnumerable<string>` parameters.
      for (const division of params.divisions) {
        search.append('divisions', division);
      }

      const query = search.toString();
      return downloadResponse(
        api.get(
          `inventory/export/${params.businessId}${query ? `?${query}` : ''}`,
          { responseType: 'blob' }
        ),
        `catalog-${new Date().toISOString().slice(0, 10)}.csv`,
        'text/csv;charset=utf-8'
      );
    },
  });
}

// -------------------------------------------------------------- shared stores

export interface SharedStore {
  id: string;
  name: string;
}

/** Stores this one's inventory is kept in step with. Paged envelope. */
export function useSharedStores(businessId: string | null) {
  return useQuery({
    queryKey: [...KEY, 'shared', businessId],
    queryFn: () =>
      get<{ results: SharedStore[] }>(`inventory/shared/${businessId}`),
    select: (data) => data.results ?? [],
    enabled: businessId !== null,
  });
}

/**
 * Link or unlink one store.
 *
 * `UpdateSharedInventoryStoreBindingModel` is a pair of ids, so this is one
 * store per call rather than a set — linking three means three requests, which
 * is why the UI adds and removes one row at a time instead of offering a
 * multi-select that would fan out behind a single Save.
 */
export function useSetSharedStore(businessId: string) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({
      sharedBusinessId,
      remove,
    }: {
      sharedBusinessId: string;
      remove: boolean;
    }) =>
      post<unknown>(
        remove ? 'inventory/shared/remove' : 'inventory/shared/create',
        { BusinessId: businessId, SharedBusinessId: sharedBusinessId }
      ),
    onSuccess: invalidate,
  });
}
